import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Github, Twitter, Mail } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

export default function Footer() {
  const { t } = useTranslation();
  const [cities, setCities] = useState([]);
  useEffect(() => {
    api
      .get("/cities")
      .then((r) => setCities(r.data || []))
      .catch(() => {});
  }, []);

  // Group cities by country for a clean SEO-friendly footer block.
  const byCountry = cities.reduce((acc, c) => {
    (acc[c.country] = acc[c.country] || []).push(c);
    return acc;
  }, {});

  return (
    <footer className="border-t border-[#DED7C7] mt-24 bg-[#E9F0E0]/40" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-14 grid md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <div className="flex items-center" data-testid="footer-logo">
            <span className="font-black text-lg tracking-tight leading-none">
              doing<span className="text-[#5B8C66]">-good</span>.org
            </span>
          </div>
          <p className="mt-4 text-sm text-[#5F6B61] max-w-md leading-relaxed">
            {t("footer.tagline")}
          </p>
          <p className="mt-3 text-xs text-[#5F6B61]" data-testid="footer-credit">
            {t("footer.credit")}
          </p>
        </div>

        <div>
          <p className="eyebrow">{t("footer.explore_label")}</p>
          <ul className="mt-4 space-y-2 text-sm">
            <li><Link to="/offers" className="hover:text-[#5B8C66]">{t("footer.all_offers")}</Link></li>
            <li><Link to="/offers?category=volunteering" className="hover:text-[#5B8C66]">{t("categories.volunteering")}</Link></li>
            <li><Link to="/offers?category=donation" className="hover:text-[#5B8C66]">{t("categories.donation")}</Link></li>
            <li><Link to="/offers?category=impact_investment" className="hover:text-[#5B8C66]">{t("categories.impact_investment")}</Link></li>
          </ul>
        </div>

        <div>
          <p className="eyebrow">{t("footer.get_involved")}</p>
          <ul className="mt-4 space-y-2 text-sm">
            <li><Link to="/list-offer" className="hover:text-[#5B8C66]">{t("footer.list_offer")}</Link></li>
            <li><Link to="/register" className="hover:text-[#5B8C66]">{t("footer.create_account")}</Link></li>
            <li><a className="hover:text-[#5B8C66] inline-flex items-center gap-1.5" href="mailto:info@doing-good.org"><Mail size={14}/> {t("footer.contact")}</a></li>
          </ul>
        </div>
      </div>

      {/* Cities by country — gives Google rich, crawlable internal links */}
      {cities.length > 0 && (
        <div className="border-t border-[#DED7C7]" data-testid="footer-cities">
          <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-10">
            <p className="eyebrow">{t("footer.cities_label", { defaultValue: "Explore by city" })}</p>
            <div className="mt-5 grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-4 text-sm">
              {Object.entries(byCountry).map(([country, list]) => (
                <div key={country}>
                  <p className="font-bold text-[#1F2A24] mb-2">{t(`countries.${country}`, { defaultValue: country })}</p>
                  <ul className="space-y-1">
                    {list.map((c) => (
                      <li key={c.slug}>
                        <Link to={`/c/${c.slug}`} className="text-[#3A463C] hover:text-[#5B8C66]" data-testid={`footer-city-${c.slug}`}>
                          {t(`cities.${c.city}`, { defaultValue: c.city })}
                          {c.count > 0 && <span className="text-[#9AA597]"> · {c.count}</span>}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="border-t border-[#DED7C7]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[#5F6B61]">
          <p>{t("footer.rights", { year: new Date().getFullYear() })}</p>
          <div className="flex items-center gap-5">
            <Link to="/about" className="hover:text-[#5B8C66]" data-testid="footer-about">{t("footer.about", { defaultValue: "About" })}</Link>
            <Link to="/impressum" className="hover:text-[#5B8C66]" data-testid="footer-imprint">{t("footer.imprint", { defaultValue: "Imprint" })}</Link>
            <Link to="/datenschutz" className="hover:text-[#5B8C66]" data-testid="footer-privacy">{t("footer.privacy", { defaultValue: "Privacy" })}</Link>
            <a href="#" aria-label="twitter" className="hover:text-[#5B8C66]"><Twitter size={16} strokeWidth={1.6}/></a>
            <a href="#" aria-label="github" className="hover:text-[#5B8C66]"><Github size={16} strokeWidth={1.6}/></a>
          </div>
        </div>
      </div>
    </footer>
  );
}
